import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Navigation, MapPin, CheckCircle2, AlertTriangle,
  ThumbsUp, Copy, ExternalLink, Share2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fetcher = (u) => api.get(u).then((r) => r.data);

const AddressFinderDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: a, mutate } = useSWR(`/address-finder/${id}`, fetcher);
  if (!a) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const openSatNav = () => {
    const lat = a.latitude, lng = a.longitude;
    if (!lat || !lng) { toast.error("No GPS point saved"); return; }
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const url = isIOS
      ? `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`
      : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, "_blank");
  };
  const openWaze = () => a.latitude && window.open(`https://waze.com/ul?ll=${a.latitude},${a.longitude}&navigate=yes`);
  const copyGps = () => {
    navigator.clipboard.writeText(`${a.latitude},${a.longitude}`);
    toast.success("Coordinates copied");
  };
  const share = () => {
    const lines = [
      a.title,
      [a.address_line_1, a.town, a.postcode].filter(Boolean).join(", "),
    ];
    if (a.latitude && a.longitude) {
      lines.push(`https://www.google.com/maps?q=${a.latitude},${a.longitude}`);
    }
    if (a.what3words) lines.push(`///${a.what3words} — https://what3words.com/${a.what3words}`);
    if (a.entrance_instructions) lines.push(`Instructions: ${a.entrance_instructions}`);
    if (a.vehicle_size_warning) lines.push(`⚠️ ${a.vehicle_size_warning}`);
    const text = encodeURIComponent(lines.filter(Boolean).join("\n"));
    // Try Web Share API first (native share sheet on mobile), fallback to WhatsApp
    if (navigator.share) {
      navigator.share({ title: a.title, text: lines.filter(Boolean).join("\n") }).catch(() => {
        window.open(`https://wa.me/?text=${text}`, "_blank");
      });
    } else {
      window.open(`https://wa.me/?text=${text}`, "_blank");
    }
  };
  const helpful = async () => { await api.post(`/address-finder/${id}/helpful`); toast.success("Thanks"); mutate(); };
  const verify = async () => { await api.post(`/address-finder/${id}/verify`); toast.success("Verified"); mutate(); };
  const canVerify = ["platform_owner", "company_admin", "depot_manager"].includes(user?.role);

  return (
    <div className="p-4 pt-6 space-y-4 pb-32">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="af-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
      </Button>

      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">{a.postcode}</div>
        <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">{a.title}</h1>
        <div className="text-sm text-slate-500 mt-1">
          {[a.property_name, a.address_line_1, a.town].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-2 flex gap-2 flex-wrap">
          {a.status === "verified" && <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Verified</Badge>}
          {a.vehicle_size_warning && <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" /> Warning</Badge>}
        </div>
      </div>

      {a.images?.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {a.images.map((im) => (
            <img key={im.id} src={im.url} alt="" className="w-full aspect-square object-cover rounded-xl" />
          ))}
        </div>
      )}

      {a.entrance_instructions && (
        <Card className="p-4 rounded-2xl border-brand-border bg-slate-50">
          <div className="text-[10px] uppercase tracking-wider text-brand-red font-mono mb-1">Entrance instructions</div>
          <div className="text-sm whitespace-pre-wrap">{a.entrance_instructions}</div>
        </Card>
      )}

      {a.vehicle_size_warning && (
        <Card className="p-4 rounded-2xl border-amber-300 bg-amber-50">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-mono mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Vehicle warning
          </div>
          <div className="text-sm">{a.vehicle_size_warning}</div>
        </Card>
      )}

      {a.driver_notes && (
        <Card className="p-4 rounded-2xl border-brand-border">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Notes</div>
          <div className="text-sm whitespace-pre-wrap">{a.driver_notes}</div>
        </Card>
      )}

      <Button onClick={openSatNav} className="w-full h-14 bg-brand-red hover:bg-brand-red-hover text-lg"
              data-testid="af-navigate-btn">
        <Navigation className="w-6 h-6 mr-2" /> Open in sat nav
      </Button>

      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" onClick={openWaze} data-testid="af-waze"><ExternalLink className="w-4 h-4 mr-1.5" /> Waze</Button>
        <Button variant="outline" onClick={copyGps} data-testid="af-copy"><Copy className="w-4 h-4 mr-1.5" /> Copy GPS</Button>
        <Button variant="outline" onClick={share} data-testid="af-share"><Share2 className="w-4 h-4 mr-1.5" /> Share</Button>
      </div>

      {a.what3words && (
        <a href={`https://what3words.com/${a.what3words}`} target="_blank" rel="noreferrer"
           className="block text-center text-sm text-brand-navy underline font-mono" data-testid="af-w3w-link">
          ///{a.what3words}
        </a>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={helpful} className="flex-1" data-testid="af-helpful">
          <ThumbsUp className="w-4 h-4 mr-1.5" /> Helpful ({a.usage_count || 0})
        </Button>
        {canVerify && a.status !== "verified" && (
          <Button onClick={verify} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="af-verify">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Verify
          </Button>
        )}
      </div>

      {a.latitude && (
        <div className="text-xs text-slate-500 font-mono text-center">
          <MapPin className="w-3 h-3 inline mr-1" />
          {a.latitude.toFixed(6)}, {a.longitude.toFixed(6)}
        </div>
      )}
    </div>
  );
};

export default AddressFinderDetail;
